/**
 * Checkout Slice
 *
 * Manages flight checkout state including:
 * - Checkout URL
 * - Prebook state
 * - Traveller validation state
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { PopupErrorDetails } from '@contracts';
import { prebookFlightThunk } from '../thunks/prebookThunks';
import { validateTravellerFormThunk } from '../thunks/reviewTravellerThunks';
import { selectAddonThunk } from '../thunks/addonsThunks';

// ============================================
// State Types
// ============================================

/**
 * @deprecated Use PopupErrorDetails from @contracts instead
 */
export type PrebookErrorDetails = PopupErrorDetails;

export interface CheckoutState {
  checkoutUrl: string | null;
  prebook: {
    isLoading: boolean;
    error: string | null;
    /** When set, show generic popup with message + description (e.g. coupon invalid) */
    errorDetails: PrebookErrorDetails | null;
  };
  travellerValidation: {
    isLoading: boolean;
    error: string | null;
  };
}

// ============================================
// Initial State
// ============================================

export const initialCheckoutState: CheckoutState = {
  checkoutUrl: null,
  prebook: {
    isLoading: false,
    error: null,
    errorDetails: null,
  },
  travellerValidation: {
    isLoading: false,
    error: null,
  },
};

// ============================================
// Slice
// ============================================

const checkoutSlice = createSlice({
  name: 'flights/checkout',
  initialState: initialCheckoutState,
  reducers: {
    setCheckoutUrl: (state, action: PayloadAction<string | null>) => {
      state.checkoutUrl = action.payload;
    },

    clearCheckoutUrl: (state) => {
      state.checkoutUrl = null;
    },

    clearPrebookError: (state) => {
      state.prebook.error = null;
      state.prebook.errorDetails = null;
    },

    resetCheckoutState: () => initialCheckoutState,
  },
  extraReducers: (builder) => {
    // Prebook Flight
    builder
      .addCase(prebookFlightThunk.pending, (state) => {
        state.prebook.isLoading = true;
        state.prebook.error = null;
      })
      .addCase(prebookFlightThunk.fulfilled, (state, action) => {
        state.prebook.isLoading = false;
        const prebookData = action.payload.data.resources[0]?.data;
        if (prebookData?.checkoutUrl) {
          state.checkoutUrl = prebookData.checkoutUrl;
          // Redirect to payment page
          window.open(prebookData.checkoutUrl, '_self');
        }
      })
      .addCase(prebookFlightThunk.rejected, (state, action) => {
        state.prebook.isLoading = false;
        const payload = action.payload;
        // Check for PopupErrorDetails format (errorMessage, errorDescription)
        if (payload && typeof payload === 'object' && 'errorMessage' in payload) {
          const errorDetails = payload as PopupErrorDetails;
          state.prebook.error = errorDetails.errorMessage;
          state.prebook.errorDetails = errorDetails;
        } else {
          state.prebook.error = typeof payload === 'string' ? payload : 'Prebook failed';
          state.prebook.errorDetails = null;
        }
      });

    // Validate Traveller Form
    builder
      .addCase(validateTravellerFormThunk.pending, (state) => {
        state.travellerValidation.isLoading = true;
        state.travellerValidation.error = null;
      })
      .addCase(validateTravellerFormThunk.fulfilled, (state) => {
        state.travellerValidation.isLoading = false;
      })
      .addCase(validateTravellerFormThunk.rejected, (state, action) => {
        state.travellerValidation.isLoading = false;
        state.travellerValidation.error = action.payload ?? 'Validation failed';
      });

    // Select Addon - clear checkout URL so prebook is called fresh
    builder.addCase(selectAddonThunk.fulfilled, (state) => {
      state.checkoutUrl = null;
    });
  },
});

// ============================================
// Actions
// ============================================

export const { setCheckoutUrl, clearCheckoutUrl, clearPrebookError, resetCheckoutState } =
  checkoutSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const checkoutReducer = checkoutSlice.reducer;
